/**
 * 설정 페이지 (프로필 관리)
 * 
 * @description
 * 로그인한 사용자의 프로필을 조회하고 수정하는 화면입니다.
 * 주 계정은 GET/PATCH /user/profile 응답을 기준으로 표시하고,
 * 전환 계정은 AuthContext의 현재 계정 정보를 표시합니다.
 * 
 * @features
 * - 프로필 정보 조회 및 수정
 * - 국가, 연락처, 소속, 직책, 직무 수정
 * - 프로필 이미지 변경 (직접 선택 + 추천 아바타)
 * - 이메일 인증/변경 및 계정 삭제 화면 이동
 * 
 * @data-source
 * - 프로필 조회: GET /user/profile
 * - 프로필 수정: PATCH /user/profile
 * - 직접 선택 이미지: POST /file/presigned-url + S3 PUT 후 fileUrl 전달
 * 
 * @api
 * - GET /user/profile (프로필 조회)
 * - PATCH /user/profile (프로필 수정)
 * - POST /file/presigned-url (uploadPath USER) — 직접 선택 이미지
 * 
 * @routes
 * - /settings (현재 페이지)
 * - /verify-email (이메일 인증)
 * - /email-change (이메일 변경)
 * - /account-deletion/request (회원 탈퇴)
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronRight, Pencil, User as UserIcon, X, Phone, Trash2, Building } from 'lucide-react';
import { Button, Input, AlertDialog } from '@/components/shared/ui';
import { useUser } from '@/data/queries/useUser';
import type { UpdateUserProfileRequestDto } from '@/types/user';
import { JobTitle } from '@/types/api/auth';
import { convertRoleNumberToString, getRoleLabel, resolveLicenseFromProfile } from '@/utils/roleUtils';
import { ROUTES } from '@/lib/constants/routes';
import { uploadFileViaPresignedUrl } from '@/services/fileService';
import { useCountries } from '@/hooks/useOrganization';
import { toCountryOptions } from '@/lib/countryList';
import {
	buildApiProfileSnapshot,
	buildAuthProfileFallback,
	buildProfileFormSnapshot,
	clearPendingProfileFormFromSession,
	clearProfileFormFallbackFromLocal,
	getBrowserCountryCode,
	getImageFileExtension,
	getProfileApiLanguageCode,
	isAbsoluteHttpOrHttpsUrl,
	pendingProfileFormCache,
	readPendingProfileFormFromSession,
	readProfileFormFallbackFromLocal,
	toProfileString,
	writePendingProfileFormToSession,
	writeProfileFormFallbackToLocal,
	type PendingProfileFormSnapshot,
	type ProfileFormSnapshot,
} from './settingsProfileUtils';

type AvatarRequireContext = {
	keys: () => string[];
	(id: string): string | { default: string };
};

type WebpackRequire = typeof require & {
	context: (path: string, deep?: boolean, filter?: RegExp) => AvatarRequireContext;
};

const avatarContext = (require as WebpackRequire).context(
	'../../assets/img/avatars',
	false,
	/\.(png|jpe?g|webp|svg)$/,
);

function normalizeAvatarKey(key: string): string {
	const decodedKey = decodeURIComponent(key);
	return decodedKey
		.toLowerCase()
		.replace(/\.(png|jpe?g|webp|svg)$/i, '')
		.replace(/[^a-z0-9]/g, '');
}

const seenAvatarKeys = new Set<string>();
const seenAvatarUrls = new Set<string>();
const PRESET_AVATARS = avatarContext
	.keys()
	.filter((key) => !/^\.\/avatar_[1-5]\.png$/i.test(key))
	.sort()
	.filter((key) => {
		const normalizedKey = normalizeAvatarKey(key);
		if (seenAvatarKeys.has(normalizedKey)) return false;
		seenAvatarKeys.add(normalizedKey);
		return true;
	})
	.map((key) => {
		const avatarModule = avatarContext(key);
		return typeof avatarModule === 'string' ? avatarModule : avatarModule.default;
	})
	.filter((avatar) => {
		const normalizedUrl = decodeURIComponent(avatar).toLowerCase();
		if (seenAvatarUrls.has(normalizedUrl)) return false;
		seenAvatarUrls.add(normalizedUrl);
		return true;
	});
const CUSTOM_JOB_ROLE_VALUE = '__custom__';
const JOB_ROLE_VALUES = [
	JobTitle.DOCTOR,
	JobTitle.NURSE,
	JobTitle.NURSING_AIDE,
	JobTitle.PARAMEDIC,
	JobTitle.EDUCATOR,
	JobTitle.STUDENT,
	JobTitle.RESEARCHER,
] as const;
const MAX_PROFILE_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

const JOB_ROLE_TRANSLATION_KEYS: Record<JobTitle, string> = {
	[JobTitle.DOCTOR]: 'doctor',
	[JobTitle.NURSE]: 'nurse',
	[JobTitle.NURSING_AIDE]: 'nurseAide',
	[JobTitle.PARAMEDIC]: 'paramedic',
	[JobTitle.EDUCATOR]: 'educator',
	[JobTitle.STUDENT]: 'student',
	[JobTitle.RESEARCHER]: 'researcher',
	[JobTitle.CUSTOM]: 'jobRoleCustom',
};

/**
 * API 응답에서 받은 jobTitle과 customJobTitle을 UI 상태로 변환
 */
function applyJobRoleFromApi(
	jobTitle: string | null | undefined,
	customJobTitle: string | null | undefined,
	setJobRole: React.Dispatch<React.SetStateAction<string>>,
	setCustomJobRole: React.Dispatch<React.SetStateAction<string>>,
): void {
	if (!jobTitle) {
		setJobRole('');
		setCustomJobRole('');
	} else if (jobTitle === JobTitle.CUSTOM) {
		setJobRole(CUSTOM_JOB_ROLE_VALUE);
		setCustomJobRole(customJobTitle?.trim() ?? '');
	} else if (Object.values(JobTitle).includes(jobTitle as JobTitle)) {
		setJobRole(jobTitle);
		setCustomJobRole('');
	} else {
		// 알 수 없는 값이면 직접입력으로 처리
		setJobRole(CUSTOM_JOB_ROLE_VALUE);
		setCustomJobRole(jobTitle);
	}
}

/**
 * 레거시: department 필드만 있는 경우 호환 (한글 값 처리)
 */
function applyJobRoleFromDepartment(
	department: string,
	setJobRole: React.Dispatch<React.SetStateAction<string>>,
	setCustomJobRole: React.Dispatch<React.SetStateAction<string>>,
): void {
	const LEGACY_JOB_ROLE_MAP: Record<string, JobTitle> = {
		'의사': JobTitle.DOCTOR,
		'간호사': JobTitle.NURSE,
		'간호조무사': JobTitle.NURSING_AIDE,
		'응급구조사': JobTitle.PARAMEDIC,
		'교육자': JobTitle.EDUCATOR,
		'학생': JobTitle.STUDENT,
		'연구원': JobTitle.RESEARCHER,
	};
	
	if (!department) {
		setJobRole('');
		setCustomJobRole('');
	} else if (LEGACY_JOB_ROLE_MAP[department]) {
		setJobRole(LEGACY_JOB_ROLE_MAP[department]);
		setCustomJobRole('');
	} else {
		setJobRole(CUSTOM_JOB_ROLE_VALUE);
		setCustomJobRole(department);
	}
}

export default function Settings(): React.ReactElement {
	const { t, i18n } = useTranslation('auth');
	// ========== API Hooks ==========
	/**
	 * 프로필 조회/수정 API.
	 */
	const { updateProfile: updateProfileApi, getProfile } = useUser();
	const { data: countriesData } = useCountries();
	
	// ========== Hooks ==========
	
    const { user, getCurrentRole, updateUserProfile } = useAuth();
    const currentAccount = user?.currentAccount;
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
	const pendingProfileFormRef = useRef<PendingProfileFormSnapshot | null>(null);

	// ========== State 관리 ==========
	
	/**
	 * 사용자 이름
	 */
    const [name, setName] = useState(user?.name || '');
	
	/**
	 * 사용자 이메일
	 * @description 프로필 응답 이메일. 인증 상태 안내와 변경 화면 이동에 사용합니다.
	 */
	const [email, setEmail] = useState<string | null>(null);

	/** 개인 프로필 국가. 서버 값이 없으면 브라우저 지역으로 기본 선택합니다. */
	const [countryId, setCountryId] = useState<number | null>(null);
	
	/** 휴대폰 번호 */
	const [phoneNumber, setPhoneNumber] = useState<string>('');

	/** 소속 */
	const [organization, setOrganization] = useState<string>('');

	/** 직책 */
	const [position, setPosition] = useState<string>('');

	/** 직무 선택값. PATCH DTO의 department로 저장합니다. */
	const [jobRole, setJobRole] = useState<string>('');

	/** 직무 직접입력 값 */
	const [customJobRole, setCustomJobRole] = useState<string>('');
	
	/**
	 * 프로필 로딩 중 상태
	 */
	const [isProfileLoading, setIsProfileLoading] = useState<boolean>(true);
	
	/**
	 * 프로필 이미지 URL 또는 미리보기 Data URL.
	 */
    const [profileImage, setProfileImage] = useState<string | null>(null);
	const [profileImageLoadError, setProfileImageLoadError] = useState<boolean>(false);
	
	/**
	 * 초기 프로필 이미지 (변경 감지용)
	 */
	const [initialProfileImage, setInitialProfileImage] = useState<string | null>(null);
	const [initialProfileForm, setInitialProfileForm] = useState<ProfileFormSnapshot | null>(null);

	/** 파일 선택으로 올린 원본 — 저장 시 presigned 업로드에 사용 */
	const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
	
	/**
	 * 선택된 추천 아바타 인덱스
	 */
    const [selectedAvatarIndex, setSelectedAvatarIndex] = useState<number | null>(null);
	
	/**
	 * 프로필 수정 진행 중 상태
	 * @description 프로필 수정 API 호출 중 버튼 비활성화
	 */
	const [isLoading, setIsLoading] = useState<boolean>(false);
	
	/**
	 * AlertDialog 상태
	 * @field isOpen - 팝업 열림 여부
	 * @field message - 표시할 메시지
	 * @field navigateToOnClose - 알림 닫은 뒤 이동할 경로 (없으면 현재 화면 유지)
	 */
	const [alertState, setAlertState] = useState<{
		isOpen: boolean;
		message: string;
		navigateToOnClose?: string | null;
	}>({
		isOpen: false,
		message: '',
		navigateToOnClose: null,
	});

	// ========== 계산된 값 ==========
	
    const userRole = getCurrentRole();
    /**
     * 정회원 인증 UI는 Guest 계정에서만 노출
     */
    const isGuestAccount =
        !isProfileLoading &&
        (currentAccount?.role === 'guest' || userRole === 'guest');
	const profileAccountKey = `${user?.currentAccount?.accountId ?? ''}:${currentAccount?.loginId ?? ''}`;
	const countryOptions = useMemo(
		() => toCountryOptions(countriesData?.countryList ?? [], i18n.resolvedLanguage || i18n.language),
		[countriesData, i18n.language, i18n.resolvedLanguage],
	);
	const browserCountryId = useMemo(() => {
		const browserCountryCode = getBrowserCountryCode();
		if (!browserCountryCode) return null;
		return countryOptions.find((country) => country.alpha2 === browserCountryCode)?.countryId ?? null;
	}, [countryOptions]);

	// ========== Helper 함수 ==========
	
	/**
	 * AlertDialog 표시 함수
	 * @param message - 표시할 메시지
	 */
	const showAlert = useCallback((message: string) => {
		setAlertState({ isOpen: true, message, navigateToOnClose: null });
	}, []);

	const rememberPendingProfileForm = useCallback((form: ProfileFormSnapshot) => {
		pendingProfileFormRef.current = {
			accountKey: profileAccountKey,
			form,
		};
		pendingProfileFormCache.set(profileAccountKey, form);
		writePendingProfileFormToSession(profileAccountKey, form);
		writeProfileFormFallbackToLocal(profileAccountKey, form);
	}, [profileAccountKey]);

	/**
	 * AlertDialog 닫기 함수
	 */
	const closeAlert = useCallback(() => {
		setAlertState((prev) => {
			const path = prev.navigateToOnClose;
			if (path) {
				setTimeout(() => navigate(path), 0);
			}
			return { isOpen: false, message: '', navigateToOnClose: null };
		});
	}, [navigate]);

	// ========== 프로필 조회 (페이지 로드 시) ==========
	
	// 프로필 조회
	useEffect(() => {
		const fetchProfile = async () => {
			if (!user) return;
			try {
				setIsProfileLoading(true);
				setProfileImageFile(null);
				const profile = await getProfile();
				const pendingProfileForm =
					pendingProfileFormRef.current?.accountKey === profileAccountKey
						? pendingProfileFormRef.current.form
						: pendingProfileFormCache.get(profileAccountKey)
							?? readPendingProfileFormFromSession(profileAccountKey)
							?? readProfileFormFallbackFromLocal(profileAccountKey);
				const fallbackProfileForm = pendingProfileForm ?? buildAuthProfileFallback(user, currentAccount);
				const nextProfileForm = buildProfileFormSnapshot(
					profile,
					{ ...fallbackProfileForm, countryId: null },
				);
				const displayedProfileForm: ProfileFormSnapshot = {
					...nextProfileForm,
					countryId: nextProfileForm.countryId ?? browserCountryId,
				};
				setName(nextProfileForm.realName);
				setEmail(profile.email);
				setCountryId(displayedProfileForm.countryId);
				setPhoneNumber(nextProfileForm.phoneNumber);
				setPosition(nextProfileForm.position);
				setOrganization(nextProfileForm.affiliation);
				setProfileImageLoadError(false);
				// jobTitle과 customJobTitle이 있으면 사용, 없으면 레거시 department 사용
				if (profile.jobTitle !== undefined) {
					applyJobRoleFromApi(profile.jobTitle, profile.customJobTitle, setJobRole, setCustomJobRole);
				} else {
					applyJobRoleFromDepartment(nextProfileForm.department, setJobRole, setCustomJobRole);
				}
				if (nextProfileForm.profileImage) {
					setProfileImage(nextProfileForm.profileImage);
					setInitialProfileImage(nextProfileForm.profileImage);
				} else {
					setProfileImage(null);
					setInitialProfileImage(null);
				}
				setInitialProfileForm(nextProfileForm);

				// 이메일 인증 후 role 변경 시 AuthContext 업데이트
				if (profile.role !== undefined) {
					const newRole = convertRoleNumberToString(profile.role);
					const currentRole = currentAccount?.role;
					if (newRole !== currentRole) {
						const newLicense = resolveLicenseFromProfile({
							role: profile.role,
							organizationId: profile.organizationId,
						});
						updateUserProfile({
							role: newRole,
							license: newLicense,
						});
					}
				}
			} catch (error: any) {
				setName('');
				setEmail(null);
				setCountryId(browserCountryId);
				setOrganization(currentAccount?.organizationName ?? '');
			} finally {
				setIsProfileLoading(false);
			}
		};
		fetchProfile();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentAccount?.loginId, profileAccountKey, browserCountryId]);

	useEffect(() => {
		setProfileImageLoadError(false);
	}, [profileImage]);

	// ========== 프로필 수정 처리 ==========
	
	/**
	 * 프로필 수정 핸들러
	 * 
	 * @description
	 * 변경된 프로필 필드만 PATCH /user/profile로 전송합니다.
	 * 서버가 요구하는 realName/language는 변경이 있을 때 항상 포함합니다.
	 * 
	 * @validation
	 * - 이름은 필수 입력입니다.
	 * 
	 * @api
	 * - PATCH /user/profile
	 * - POST /file/presigned-url (직접 선택 이미지)
	 * - 성공 후 GET /user/profile로 최신 값을 재조회합니다.
	 */
    const handleConfirm = async () => {
		// ========== 1단계: 유효성 검사 ==========
		
		// 이름 입력 여부 확인
		if (!name || name.trim() === '') {
			showAlert(t('settings.alertNameRequired'));
			return;
		}

		// ========== 2단계: 프로필 수정 API 호출 ==========
		
		setIsLoading(true);

		try {
			// 프로필 수정 요청 데이터
			const finalDepartment =
				jobRole === CUSTOM_JOB_ROLE_VALUE
					? customJobRole.trim()
					: jobRole.trim();
			
			// jobTitle과 customJobTitle 계산
			const finalJobTitle: JobTitle | null = 
				!jobRole || jobRole === ''
					? null
					: jobRole === CUSTOM_JOB_ROLE_VALUE
						? JobTitle.CUSTOM
						: (jobRole as JobTitle);
			
			const finalCustomJobTitle = 
				jobRole === CUSTOM_JOB_ROLE_VALUE
					? customJobRole.trim()
					: '';

			const nextProfileForm: ProfileFormSnapshot = {
				realName: name.trim(),
				countryId,
				phoneNumber: phoneNumber.trim(),
				affiliation: organization.trim(),
				position: position.trim(),
				department: finalDepartment, // 레거시 호환용
				jobTitle: finalJobTitle,
				customJobTitle: finalCustomJobTitle,
				profileImage: profileImage ?? null,
			};

			// 전체 프로필 변경 여부 확인
			const hasAnyFieldChanged = !initialProfileForm || 
				nextProfileForm.realName !== initialProfileForm.realName ||
				nextProfileForm.countryId !== initialProfileForm.countryId ||
				nextProfileForm.phoneNumber !== initialProfileForm.phoneNumber ||
				nextProfileForm.affiliation !== initialProfileForm.affiliation ||
				nextProfileForm.position !== initialProfileForm.position ||
				nextProfileForm.jobTitle !== initialProfileForm.jobTitle ||
				nextProfileForm.customJobTitle !== initialProfileForm.customJobTitle;

			const hasProfileImageChanged =
				!initialProfileForm || nextProfileForm.profileImage !== initialProfileForm.profileImage;

			const updateData: UpdateUserProfileRequestDto = {};

			// 프로필 이미지 업로드 처리
			if (hasProfileImageChanged && profileImage) {
				if (profileImageFile) {
					try {
						updateData.profileImageUrl = await uploadFileViaPresignedUrl(profileImageFile, 'USER');
					} catch {
						showAlert(t('settings.alertProfileImageUploadFailed'));
						setIsLoading(false);
						return;
					}
				} else if (profileImage.startsWith('data:') || profileImage.startsWith('/') || profileImage.startsWith(window.location.origin)) {
					try {
						const res = await fetch(profileImage);
						const blob = await res.blob();
						const ext = getImageFileExtension(blob.type);
						const file = new File([blob], `profile.${ext}`, { type: blob.type || 'image/jpeg' });
						updateData.profileImageUrl = await uploadFileViaPresignedUrl(file, 'USER');
					} catch {
						showAlert(t('settings.alertProfileImageUploadFailed'));
						setIsLoading(false);
						return;
					}
				} else {
					updateData.profileImageUrl = profileImage;
				}
			}

			// 어떤 필드라도 변경되면 API 호출
			const hasProfileChanges = hasAnyFieldChanged || hasProfileImageChanged;
			if (hasProfileChanges) {
				// 모든 필드를 전송 (백엔드가 보낸 필드만 유지하므로)
				updateData.realName = nextProfileForm.realName;
				updateData.countryId = nextProfileForm.countryId;
				updateData.phoneNumber = toProfileString(nextProfileForm.phoneNumber);
				updateData.affiliation = toProfileString(nextProfileForm.affiliation);
				updateData.position = toProfileString(nextProfileForm.position);
				updateData.jobTitle = finalJobTitle ? finalJobTitle : undefined;
				updateData.customJobTitle = toProfileString(nextProfileForm.customJobTitle);
				updateData.language = getProfileApiLanguageCode(i18n.resolvedLanguage || i18n.language);
				
				// 프로필 이미지: 새로 업로드한 게 없으면 기존 이미지 유지
				if (!('profileImageUrl' in updateData) && isAbsoluteHttpOrHttpsUrl(initialProfileForm?.profileImage)) {
					updateData.profileImageUrl = initialProfileForm.profileImage;
				}
				
				await updateProfileApi(updateData);
			}

		// 성공 메시지 표시 (닫을 때 홈으로 이동)
		setAlertState({
			isOpen: true,
			message: t('settings.alertProfileUpdated'),
			navigateToOnClose: ROUTES.HOME,
		});
		
		// 프로필 데이터 다시 조회하여 변경사항 반영 (주 계정일 때만; 계정 전환 상태에서는 AuthContext 덮어쓰지 않음)
		if (hasProfileChanges) {
			try {
				const updatedProfile = await getProfile();
				const fallbackProfileForm: ProfileFormSnapshot = {
					...nextProfileForm,
					profileImage: updateData.profileImageUrl ?? nextProfileForm.profileImage,
				};
				const derivedUpdatedProfileForm = buildProfileFormSnapshot(updatedProfile, fallbackProfileForm);
				setName(derivedUpdatedProfileForm.realName);
				setEmail(updatedProfile.email);
				setCountryId(derivedUpdatedProfileForm.countryId ?? countryId);
				setPhoneNumber(derivedUpdatedProfileForm.phoneNumber);
				setOrganization(derivedUpdatedProfileForm.affiliation);
				setPosition(derivedUpdatedProfileForm.position);
				// jobTitle과 customJobTitle이 있으면 사용, 없으면 레거시 department 사용
				if (updatedProfile.jobTitle !== undefined) {
					applyJobRoleFromApi(updatedProfile.jobTitle, updatedProfile.customJobTitle, setJobRole, setCustomJobRole);
				} else {
					applyJobRoleFromDepartment(derivedUpdatedProfileForm.department, setJobRole, setCustomJobRole);
				}
				setProfileImageFile(null);
				if (derivedUpdatedProfileForm.profileImage) {
					setProfileImage(derivedUpdatedProfileForm.profileImage);
					setInitialProfileImage(derivedUpdatedProfileForm.profileImage);
				} else {
					setProfileImage(null);
					setInitialProfileImage(null);
				}
				setInitialProfileForm(derivedUpdatedProfileForm);
				rememberPendingProfileForm(derivedUpdatedProfileForm);
				const updatedRole = convertRoleNumberToString(updatedProfile.role);
				const updatedLicense = resolveLicenseFromProfile(updatedProfile);
				updateUserProfile({
					name: buildApiProfileSnapshot(updatedProfile).realName,
					email: updatedProfile.email ?? '',
					profileImageUrl: buildApiProfileSnapshot(updatedProfile).profileImage,
					role: updatedRole,
					license: updatedLicense,
				});
			} catch {}
		} else {
			setInitialProfileForm(nextProfileForm);
			rememberPendingProfileForm(nextProfileForm);
		}
		} catch (error: unknown) {
			const msg =
				error instanceof Error && error.message.trim()
					? error.message
					: t('settings.alertProfileError');
			showAlert(msg);
		} finally {
			// 항상 실행: 로딩 상태 종료
			setIsLoading(false);
		}
    };

	/**
	 * 취소 버튼 핸들러
	 * @description 홈으로 이동
	 */
    const handleCancel = () => {
        navigate(ROUTES.HOME);
    };

	/**
	 * 이메일 인증 버튼 핸들러
	 * @description 이메일 인증 페이지로 이동
	 */
    const handleVerifyEmail = () => {
        navigate('/verify-email');
    };

	/**
	 * 이메일 변경 버튼 핸들러
	 * @description 이메일 변경 페이지로 이동
	 */
    const handleEmailChange = () => {
        navigate(ROUTES.AUTH.EMAIL_CHANGE);
    };

	/**
	 * 추천 아바타 선택 핸들러
	 * @param index - 선택한 아바타 인덱스
	 * @description 추천 아바타 중 하나를 선택하면 프로필 이미지로 설정
	 * @note 번들된 이미지 URL은 저장 시 presigned 업로드를 통해 서버 URL로 변환합니다.
	 */
    const handleAvatarSelect = (index: number) => {
        // 예: /static/media/profile_avatar.abc123.png
        setProfileImageFile(null);
        setProfileImage(PRESET_AVATARS[index]);
		setProfileImageLoadError(false);
        setSelectedAvatarIndex(index);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

	/**
	 * 이미지 선택 버튼 핸들러
	 * @description 파일 선택 input을 트리거하여 이미지 파일 선택 대화상자 열기
	 */
    const handleImageSelect = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
        fileInputRef.current?.click();
    };

	/**
	 * 이미지 파일 변경 핸들러
	 * @param e - 파일 input 변경 이벤트
	 * @description 선택한 이미지 파일을 Data URL로 변환하여 미리보기 표시
	 */
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

		if (!file.type.startsWith('image/')) {
			showAlert(t('settings.alertProfileImageInvalidType'));
			e.currentTarget.value = '';
			return;
		}

		if (file.size > MAX_PROFILE_IMAGE_SIZE_BYTES) {
			showAlert(t('settings.alertProfileImageTooLarge'));
			e.currentTarget.value = '';
			return;
		}

		setProfileImageFile(file);
		setProfileImageLoadError(false);
		const reader = new FileReader();
		reader.onloadend = () => {
			setProfileImage(reader.result as string);
			setSelectedAvatarIndex(null); // Clear preset selection when file is chosen
		};
		reader.readAsDataURL(file);
    };

	/**
	 * 이미지 리셋 핸들러
	 * @description 선택한 프로필 이미지를 초기화
	 */
    const handleImageReset = () => {
        setProfileImageFile(null);
        setProfileImage(initialProfileImage);
		setProfileImageLoadError(false);
        setSelectedAvatarIndex(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

	/**
	 * 계정 삭제 버튼 핸들러
	 * @description 회원 탈퇴 요청 페이지로 이동
	 */
    const handleDeleteAccount = () => {
        navigate('/account-deletion/request');
    };

    return (
        <>
            {/* 프로필 로딩 중 */}
            {isProfileLoading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
                    <p className="text-gray-500">{t('settings.loading')}</p>
                </div>
            ) : (
            <div className="flex flex-col items-center px-4 py-6 sm:px-6 sm:py-8">
                {/* 페이지 타이틀 */}
                <div className="w-full max-w-[460px] text-center mb-5 sm:mb-6">
                    <h1 className="text-lg sm:text-xl font-semibold text-brand-500 mb-1 sm:mb-2">{t('settings.pageTitle')}</h1>
                </div>
                <div className="w-full max-w-[460px] bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] border border-gray-100">
                    {/* 소속기관 및 권한 (라이선스 배지는 표시하지 않음) */}
                    {user?.currentAccount && (
                        <div className="flex items-center justify-between pb-6 mb-6">
                            <div className="flex items-center gap-2">
                                <Building size={20} className="text-gray-500" />
                                <span className="text-lg font-semibold text-gray-700">
                                    {user.currentAccount.organizationName && user.currentAccount.organizationName !== '연결된 계정'
                                        ? user.currentAccount.organizationName
                                        : user.currentAccount.role === 'admin'
                                            ? t('newbaseOrganization', { defaultValue: '뉴베이스' })
                                            : t('personalUser', { defaultValue: '개인 사용자' })}
                                </span>
                            </div>
                            <div className="px-4 py-1 text-semibold border border-blue-500 text-blue-500 rounded-lg">
                                <span className="text-base font-semibold text-blue-700">
                                    {t(`settings.roles.${user.currentAccount.role}`, {
                                        defaultValue: getRoleLabel(user.currentAccount.role),
                                    })}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* 기본 정보 섹션 */}
                    <div className="space-y-6">
                    {/* Name Field */}
                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                            {t('settings.nameLabel')} <span className="text-red-500">*</span>
                        </label>
                        <Input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="border-gray-200 rounded-xl text-gray-900"
                            placeholder={t('settings.namePlaceholder')}
                        />
                    </div>

                    {/* ID Field (Read-only) */}
                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                            {t('settings.idLabel')}
                        </label>
                        <div className="w-full px-5 py-2.5 bg-gray-50 rounded-2xl">
                            <span className="text-gray-900 font-medium text-base">
                                {currentAccount?.loginId || (currentAccount?.accountId && !String(currentAccount.accountId).startsWith('acc_') ? currentAccount.accountId : null) || '-'}
                            </span>
                        </div>
                    </div>

                    {/* Email Field (Conditional) */}
                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                            {t('settings.emailLabel')}
                        </label>
                        {isGuestAccount ? (
                            <button
                                onClick={handleVerifyEmail}
                                className="w-full flex items-center justify-between px-5 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-2xl transition-all group"
                            >
                                <span className="text-gray-900 font-medium text-base">{t('settings.verifyMember')}</span>
                                <div className="p-1.5 bg-gray-200 rounded-lg group-hover:bg-gray-300 transition-colors">
                                    <ChevronRight size={18} className="text-gray-600" />
                                </div>
                            </button>
                        ) : (
                            <div className="w-full flex items-center justify-between px-5 py-2.5 bg-gray-50 rounded-2xl">
                                <span className="text-gray-900 font-medium text-base">{email || t('settings.noEmail')}</span>
                                <button
                                    onClick={handleEmailChange}
                                    className="p-1.5 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                                >
                                    <Pencil size={18} className="text-gray-600" />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* 국가 */}
                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                            {t('settings.countryLabel')}
                        </label>
                        <select
                            value={countryId ?? ''}
                            onChange={(e) => setCountryId(e.target.value ? Number(e.target.value) : null)}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-base bg-white focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all appearance-none cursor-pointer text-gray-900"
                        >
                            <option value="">{t('settings.countrySelect')}</option>
                            {countryOptions.map((country) => (
                                <option key={country.countryId} value={country.countryId}>
                                    {country.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 휴대폰번호 */}
                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                            {t('settings.phoneNumberLabel')}
                        </label>
                        <div className="relative">
                            <Phone size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 pointer-events-none" />
                            <Input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                placeholder="01000000000"
                                className="pl-11 border-gray-200 rounded-xl text-gray-900"
                            />
                        </div>
                    </div>

                    {/* 소속 */}
                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                            {t('settings.organizationLabel')}
                        </label>
                        <Input
                            type="text"
                            value={organization}
                            onChange={(e) => setOrganization(e.target.value)}
                            placeholder={t('settings.organizationPlaceholder')}
                            className="border-gray-200 rounded-xl text-gray-900"
                        />
                    </div>

                    {/* 직책 */}
                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                            {t('settings.positionLabel')}
                        </label>
                        <Input
                            type="text"
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                            placeholder={t('settings.positionPlaceholder')}
                            className="border-gray-200 rounded-xl text-gray-900"
                        />
                    </div>

                    {/* 직무 */}
                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                            {t('settings.jobRoleLabel')}
                        </label>
                        <select
                            value={jobRole}
                            onChange={(e) => {
                                const value = e.target.value;
                                setJobRole(value);
                                if (value !== CUSTOM_JOB_ROLE_VALUE) {
                                    setCustomJobRole('');
                                }
                            }}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-base bg-white focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all appearance-none cursor-pointer text-gray-900"
                        >
                            <option value="">{t('settings.jobRoleSelect')}</option>
                            {JOB_ROLE_VALUES.map((value) => (
                                <option key={value} value={value}>
                                    {t(`settings.${JOB_ROLE_TRANSLATION_KEYS[value]}`)}
                                </option>
                            ))}
                            <option value={CUSTOM_JOB_ROLE_VALUE}>{t('settings.jobRoleCustom')}</option>
                        </select>
                    </div>

                    {/* 직무 직접입력 */}
                    {jobRole === CUSTOM_JOB_ROLE_VALUE && (
                        <div>
                            <label className="block mb-2 text-sm font-medium text-gray-900">
                                {t('settings.jobRoleCustomLabel')}
                            </label>
                            <Input
                                type="text"
                                value={customJobRole}
                                onChange={(e) => setCustomJobRole(e.target.value)}
                                placeholder={t('settings.jobRolePlaceholderCustom')}
                                className="border-gray-200 rounded-xl text-gray-900"
                            />
                        </div>
                    )}

                    {/* Profile Image Field */}
                    <div>
                        <label className="block mb-3 text-sm font-medium text-gray-900">
                            {t('settings.profileImageLabel')}
                        </label>
                        <div className="flex items-start gap-4">
                            <div className="relative group">
                                <button
									type="button"
									onClick={handleImageSelect}
									className="relative w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200 cursor-pointer"
									aria-label={t('settings.profileImageChange')}
									disabled={isLoading}
								>
                                    {profileImage && profileImage.trim() !== '' && !profileImageLoadError ? (
                                        <img 
                                            src={profileImage} 
                                            alt={t('settings.profileAlt')} 
                                            className="w-full h-full object-cover"
                                            onError={() => setProfileImageLoadError(true)}
                                        />
                                    ) : (
                                        <UserIcon size={40} className="text-gray-300" />
                                    )}
                                    
                                    {/* Hover Overlay */}
                                    <div 
                                        className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                                    >
                                        <Pencil size={20} className="text-white" />
                                    </div>
                                </button>
                            </div>
                            
                            <div className="flex-1">
                                <div className="flex flex-wrap gap-2">
                                    {PRESET_AVATARS.map((avatar, index) => (
                                        <button
											type="button"
                                            key={index}
                                            onClick={() => handleAvatarSelect(index)}
											disabled={isLoading}
                                            className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-all ${
                                                selectedAvatarIndex === index
                                                    ? 'border-brand-500 ring-2 ring-brand-100'
                                                    : 'border-transparent hover:border-gray-200'
                                            }`}
                                        >
                                            <img
                                                src={avatar}
                                                alt={`Avatar ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
								<div className="mt-3 flex flex-wrap gap-2">
									<Button
										type="button"
										variant="secondary"
										size="sm"
										onClick={handleImageSelect}
										disabled={isLoading}
									>
										{t('settings.profileImageSelect')}
									</Button>
									{profileImage !== initialProfileImage && (
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={handleImageReset}
											disabled={isLoading}
											className="gap-1"
										>
											<X size={14} />
											{t('settings.profileImageCancelSelection')}
										</Button>
									)}
								</div>
								<p className="mt-2 text-xs text-gray-500">
									{t('settings.profileImageHelp')}
								</p>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleImageChange}
                                    accept="image/*"
                                    className="hidden"
									disabled={isLoading}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                    {/* 버튼 */}
                    <div className="flex gap-2 sm:gap-3 mt-8">
                        <Button
                            onClick={handleConfirm}
                            variant="primary"
                            className="flex-1 h-10 sm:h-11 rounded-full text-sm sm:text-base shadow-lg shadow-brand-100 active:scale-95"
                            disabled={isLoading}
                        >
                            {isLoading ? t('settings.saving') : t('settings.save')}
                        </Button>
                        <Button
                            onClick={handleCancel}
                            variant="secondary"
                            className="flex-1 h-10 sm:h-11 rounded-full text-sm sm:text-base active:scale-95"
                            disabled={isLoading}
                        >
                            {t('settings.cancel')}
                        </Button>
                    </div>
                </div>

                {/* 계정 삭제 */}
                <div className="w-full max-w-[460px] mt-4 flex justify-end">
                    <Button
                        onClick={handleDeleteAccount}
                        variant="ghost"
                        className="text-sm text-gray-500 hover:text-red-600 flex items-center gap-2"
                    >
                        <Trash2 size={16} />
                        {t('settings.deleteAccount')}
                    </Button>
                </div>
            </div>
            )}

            <AlertDialog
                isOpen={alertState.isOpen}
                onClose={closeAlert}
                message={alertState.message}
            />
        </>
    );
}